import React, { useState, useEffect, useRef } from 'react';
import { Navigate } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { purchasesAPI, usersAPI } from '../services/api';
import VideoCard from '../components/VideoCard';
import { resizeImageToDataUrl } from '../utils/imageResize';

const SOCIAL_PLATFORMS = ['instagram', 'twitter', 'youtube', 'tiktok'];

// ────────────────────────────────────────────────────────────────
// Animation variants
// ────────────────────────────────────────────────────────────────

const staggerContainer = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.09, delayChildren: 0.1 },
  },
};

const fadeUp = {
  hidden: { opacity: 0, y: 14 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: [0.19, 1, 0.22, 1] },
  },
};

const avatarReveal = {
  hidden: { opacity: 0, scale: 0.8 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.55, ease: [0.19, 1, 0.22, 1] },
  },
};

const Profile = () => {
  const { user, updateUser, loading: authLoading } = useAuth();
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const reduced = useReducedMotion();

  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const [editDisplayName, setEditDisplayName] = useState('');
  const [editBio, setEditBio] = useState('');
  const [editSocialLinks, setEditSocialLinks] = useState({});
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [avatarDataUrl, setAvatarDataUrl] = useState(null);
  const [hasAvatar, setHasAvatar] = useState(false);
  const [avatarBust, setAvatarBust] = useState(0);

  const fileInputRef = useRef(null);

  useEffect(() => {
    if (user) {
      fetchPurchases();
      loadAvatarMeta();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const loadAvatarMeta = async () => {
    try {
      const res = await usersAPI.getPublicProfile(user.id);
      setHasAvatar(!!(res.data && res.data.has_avatar));
    } catch (err) {
      // non-fatal: fall back to the Google avatar
    }
  };

  const fetchPurchases = async () => {
    try {
      const response = await purchasesAPI.getAll();
      setPurchases(response.data);
    } catch (error) {
      console.error('Error fetching purchases:', error);
    } finally {
      setLoading(false);
    }
  };

  const startEditing = () => {
    setEditDisplayName(user.display_name || user.username || '');
    setEditBio(user.bio || '');
    const links = {};
    SOCIAL_PLATFORMS.forEach(p => {
      links[p] = (user.social_links && user.social_links[p]) || '';
    });
    setEditSocialLinks(links);
    setAvatarPreview(null);
    setAvatarDataUrl(null);
    setSaveError(null);
    setSaveSuccess(false);
    setEditing(true);
  };

  const cancelEditing = () => {
    setEditing(false);
    setSaveError(null);
  };

  const handleAvatarChange = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    try {
      const dataUrl = await resizeImageToDataUrl(file);
      setAvatarPreview(dataUrl);
      setAvatarDataUrl(dataUrl);
    } catch (err) {
      setSaveError('Could not process image. Please try a different file.');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);
    setSaveSuccess(false);
    try {
      const payload = {
        display_name: editDisplayName,
        bio: editBio,
        social_links: editSocialLinks,
      };
      if (avatarDataUrl) {
        payload.avatar = avatarDataUrl;
      }
      const res = await usersAPI.updateProfile(payload);
      // Reflect the saved values in the cached user immediately.
      updateUser({
        display_name: res.data.display_name,
        bio: res.data.bio,
        social_links: res.data.social_links,
      });
      setSaveSuccess(true);
      setEditing(false);
      if (avatarDataUrl) {
        setHasAvatar(true);
        setAvatarBust(Date.now());
      }
    } catch (err) {
      const msgs = err.response && err.response.data && err.response.data.errors;
      setSaveError(msgs ? msgs.join(', ') : 'Failed to save profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" />;
  }

  const uploadedAvatarSrc = hasAvatar ? `${usersAPI.getAvatar(user.id)}?v=${avatarBust}` : null;
  const displayAvatarSrc = uploadedAvatarSrc || user.avatar_url;

  const roleLabel = (user.role === 'admin' || user.role === 2) ? 'Admin'
    : (user.role === 'artist' || user.role === 1) ? 'Artist'
    : 'Viewer';

  return (
    <div className="min-h-screen py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* User Info */}
        <div className="bg-battle-gray rounded-lg p-6 mb-8 border border-gray-800">
          {editing ? (
            <div data-testid="profile-edit-form">
              <h2 className="text-xl font-bold text-white mb-6">Edit Profile</h2>

              {/* Avatar upload */}
              <div className="flex items-center space-x-4 mb-6">
                {avatarPreview ? (
                  <img
                    src={avatarPreview}
                    alt="Preview"
                    className="w-20 h-20 rounded-full object-cover"
                    data-testid="avatar-preview"
                  />
                ) : displayAvatarSrc ? (
                  <img
                    src={displayAvatarSrc}
                    alt={user.username}
                    className="w-20 h-20 rounded-full object-cover"
                    onError={e => { if (uploadedAvatarSrc && user.avatar_url && e.target.src !== user.avatar_url) e.target.src = user.avatar_url; }}
                  />
                ) : (
                  <div className="w-20 h-20 rounded-full bg-army flex items-center justify-center text-white text-2xl font-bold">
                    {user.username.charAt(0).toUpperCase()}
                  </div>
                )}
                <div>
                  <button
                    type="button"
                    onClick={() => fileInputRef.current && fileInputRef.current.click()}
                    className="btn-secondary text-sm"
                    data-testid="avatar-upload-btn"
                  >
                    Change Avatar
                  </button>
                  <p className="text-gray-500 text-xs mt-1">Max 512px, JPEG (auto-resized)</p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleAvatarChange}
                    data-testid="avatar-file-input"
                  />
                </div>
              </div>

              {/* Display name */}
              <div className="mb-4">
                <label className="block text-gray-400 text-sm mb-1">Display Name</label>
                <input
                  type="text"
                  value={editDisplayName}
                  onChange={e => setEditDisplayName(e.target.value)}
                  className="w-full bg-battle-dark border border-gray-700 rounded px-3 py-2 text-white focus:outline-none focus:border-army"
                  data-testid="display-name-input"
                />
              </div>

              {/* Bio */}
              <div className="mb-4">
                <label className="block text-gray-400 text-sm mb-1">Bio <span className="text-gray-600">(max 1000 chars)</span></label>
                <textarea
                  value={editBio}
                  onChange={e => setEditBio(e.target.value)}
                  rows={4}
                  maxLength={1000}
                  className="w-full bg-battle-dark border border-gray-700 rounded px-3 py-2 text-white focus:outline-none focus:border-army resize-none"
                  data-testid="bio-input"
                />
                <p className="text-gray-600 text-xs text-right">{editBio.length}/1000</p>
              </div>

              {/* Social links */}
              <div className="mb-6">
                <label className="block text-gray-400 text-sm mb-2">Social Links</label>
                {SOCIAL_PLATFORMS.map(platform => (
                  <div key={platform} className="flex items-center space-x-2 mb-2">
                    <span className="text-gray-500 text-sm w-20 capitalize">{platform}</span>
                    <input
                      type="text"
                      value={editSocialLinks[platform] || ''}
                      onChange={e => setEditSocialLinks(prev => ({ ...prev, [platform]: e.target.value }))}
                      placeholder={platform === 'website' ? 'yoursite.com' : '@handle or link'}
                      className="flex-1 bg-battle-dark border border-gray-700 rounded px-3 py-1 text-white text-sm focus:outline-none focus:border-army"
                      data-testid={`social-input-${platform}`}
                    />
                  </div>
                ))}
              </div>

              {saveError && (
                <div className="mb-4 p-3 bg-red-900/40 border border-red-700 rounded text-red-300 text-sm" data-testid="save-error">
                  {saveError}
                </div>
              )}

              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saving}
                  className="btn-primary"
                  data-testid="save-profile-btn"
                >
                  {saving ? 'Saving...' : 'Save Profile'}
                </button>
                <button
                  type="button"
                  onClick={cancelEditing}
                  disabled={saving}
                  className="btn-secondary"
                  data-testid="cancel-edit-btn"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <motion.div
              variants={staggerContainer}
              initial={reduced ? 'visible' : 'hidden'}
              animate="visible"
            >
              {saveSuccess && (
                <motion.div
                  variants={fadeUp}
                  className="mb-5 p-3 bg-green-900/40 border border-green-700 rounded text-green-300 text-sm"
                  data-testid="save-success"
                >
                  Profile saved successfully.
                </motion.div>
              )}

              {/* Avatar + name row */}
              <div className="flex flex-col sm:flex-row items-center sm:items-start gap-5 mb-6">
                <motion.div variants={avatarReveal} className="flex-shrink-0">
                  {displayAvatarSrc ? (
                    <img
                      src={displayAvatarSrc}
                      alt={user.username}
                      className="w-24 h-24 rounded-full object-cover border-4 border-army/40"
                      data-testid="user-avatar"
                      onError={e => { if (uploadedAvatarSrc && user.avatar_url && e.target.src !== user.avatar_url) e.target.src = user.avatar_url; }}
                    />
                  ) : (
                    <div className="w-24 h-24 rounded-full bg-army/20 border-4 border-army/40 flex items-center justify-center text-white text-3xl font-black">
                      {user.username.charAt(0).toUpperCase()}
                    </div>
                  )}
                </motion.div>

                <div className="flex-1 text-center sm:text-left">
                  <motion.h1
                    variants={fadeUp}
                    className="font-display text-2xl md:text-3xl font-black uppercase tracking-tight text-white mb-0.5"
                    data-testid="user-name"
                  >
                    {user.display_name || user.username}
                  </motion.h1>
                  <motion.p variants={fadeUp} className="text-gray-500 text-sm mb-2">
                    @{user.username}
                  </motion.p>

                  <motion.span
                    variants={fadeUp}
                    className="inline-block bg-battle-dark border border-army/40 px-3 py-0.5 rounded-full text-xs font-bold uppercase tracking-wider text-gray-400"
                  >
                    {roleLabel}
                  </motion.span>
                </div>

                <motion.button
                  variants={fadeUp}
                  type="button"
                  onClick={startEditing}
                  className="btn-secondary text-sm self-start"
                  data-testid="edit-profile-btn"
                  whileHover={reduced ? {} : { scale: 1.04 }}
                  transition={{ duration: 0.18 }}
                >
                  Edit Profile
                </motion.button>
              </div>

              {/* Bio */}
              {user.bio && (
                <motion.div variants={fadeUp} className="mb-5 border-t border-army/20 pt-5">
                  <p className="text-gray-300 text-sm leading-relaxed whitespace-pre-wrap" data-testid="profile-bio">
                    {user.bio}
                  </p>
                </motion.div>
              )}

              {/* Social links */}
              {user.social_links && Object.values(user.social_links).some(v => v) && (
                <motion.div
                  variants={fadeUp}
                  className="flex flex-wrap gap-3 mb-5 border-t border-army/20 pt-5"
                  data-testid="profile-social-links"
                >
                  {['instagram', 'twitter', 'youtube', 'tiktok', 'website'].map(platform => {
                    const url = user.social_links[platform];
                    if (!url) return null;
                    const platformLabels = {
                      instagram: 'Instagram', twitter: 'X / Twitter', youtube: 'YouTube',
                      tiktok: 'TikTok', website: 'Website',
                    };
                    return (
                      <motion.a
                        key={platform}
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer nofollow"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-battle-dark border border-army/30 text-gray-400 hover:text-brand-yellow hover:border-brand-yellow/30 text-xs font-medium transition-colors duration-200"
                        data-testid={`profile-social-${platform}`}
                        whileHover={reduced ? {} : { scale: 1.06 }}
                        transition={{ duration: 0.16 }}
                      >
                        {platformLabels[platform]}
                      </motion.a>
                    );
                  })}
                </motion.div>
              )}

              {/* Member since */}
              <motion.p
                variants={fadeUp}
                className="text-gray-600 text-xs border-t border-army/20 pt-4"
                data-testid="profile-member-since"
              >
                Member since {new Date(user.created_at || Date.now()).toLocaleDateString('en-US', { year: 'numeric', month: 'long' })}
              </motion.p>
            </motion.div>
          )}
        </div>

        {/* Purchased Videos */}
        <div>
          <motion.h2
            className="text-2xl font-bold text-white mb-6"
            initial={reduced ? { opacity: 1 } : { opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3, ease: [0.19, 1, 0.22, 1] }}
          >
            My Purchases
          </motion.h2>
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="card animate-pulse">
                  <div className="aspect-video bg-gray-800"></div>
                  <div className="p-4 space-y-3">
                    <div className="h-4 bg-gray-800 rounded"></div>
                    <div className="h-3 bg-gray-800 rounded w-2/3"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : purchases.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="purchased-videos">
              {purchases.map((purchase) => (
                <VideoCard key={purchase.id} video={purchase.video} />
              ))}
            </div>
          ) : (
            <div className="bg-battle-gray rounded-lg p-12 text-center border border-gray-800">
              <svg className="w-16 h-16 text-gray-700 mx-auto mb-4" fill="currentColor" viewBox="0 0 20 20">
                <path d="M4 3a2 2 0 100 4h12a2 2 0 100-4H4z" />
                <path fillRule="evenodd" d="M3 8h14v7a2 2 0 01-2 2H5a2 2 0 01-2-2V8zm5 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z" clipRule="evenodd" />
              </svg>
              <p className="text-gray-500 text-lg">No purchases yet</p>
              <p className="text-gray-600 mt-2">Browse exclusive battles to start your collection</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Profile;
